import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Alert } from 'antd';

import AdminHeader from '../../components/AdminHeader';
import { useAuth } from '../../api/Auth';
import { getApiErrorMessage } from '../../api/api';

const ChangePassword: React.FC = () => {
  const { changePassword } = useAuth();

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  );
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field, value } = e.target;
    setPasswordForm({ ...passwordForm, [field]: value });
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: '' });
    }
  };

  const validatePasswordForm = () => {
    const errors: Record<string, string> = {};
    if (!passwordForm.oldPassword) {
      errors.oldPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      errors.confirmNewPassword = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;

    setChangingPassword(true);
    setPasswordMessage(null);

    try {
      await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      setPasswordMessage({
        type: 'success',
        text: 'Password changed successfully!',
      });
      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    } catch (err) {
      setPasswordMessage({
        type: 'error',
        text: getApiErrorMessage(
          err,
          'Failed to change password. Please try again.'
        ),
      });
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <AdminHeader>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <Link
              to="/admin/profile"
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to Profile
            </Link>
            <h1 className="text-2xl font-semibold text-gray-900 mt-2">
              Change Password
            </h1>
          </div>

          <form
            onSubmit={handleChangePassword}
            className="bg-white shadow rounded-lg p-6"
          >
            {passwordMessage && (
              <Alert
                message={passwordMessage.text}
                type={passwordMessage.type}
                showIcon
                className="mb-6"
              />
            )}

            <p className="text-sm text-gray-500 mb-6">
              Update your account password. Use at least 8 characters.
            </p>

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <label
                  htmlFor="oldPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Current Password
                </label>
                <Input.Password
                  id="oldPassword"
                  name="oldPassword"
                  autoComplete="current-password"
                  value={passwordForm.oldPassword}
                  onChange={handlePasswordChange}
                  status={passwordErrors.oldPassword ? 'error' : undefined}
                />
                {passwordErrors.oldPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    {passwordErrors.oldPassword}
                  </p>
                )}
              </div>

              <div className="sm:col-span-4">
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  New Password
                </label>
                <Input.Password
                  id="newPassword"
                  name="newPassword"
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  status={passwordErrors.newPassword ? 'error' : undefined}
                />
                {passwordErrors.newPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    {passwordErrors.newPassword}
                  </p>
                )}
              </div>

              <div className="sm:col-span-4">
                <label
                  htmlFor="confirmNewPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirm New Password
                </label>
                <Input.Password
                  id="confirmNewPassword"
                  name="confirmNewPassword"
                  autoComplete="new-password"
                  value={passwordForm.confirmNewPassword}
                  onChange={handlePasswordChange}
                  status={
                    passwordErrors.confirmNewPassword ? 'error' : undefined
                  }
                />
                {passwordErrors.confirmNewPassword && (
                  <p className="mt-2 text-sm text-red-600">
                    {passwordErrors.confirmNewPassword}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                type="primary"
                htmlType="submit"
                loading={changingPassword}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </AdminHeader>
  );
};

export default ChangePassword;
